import { PriceOperation } from "namirasoft-core";
import { InteractionRow, InteractionStatus } from "namirasoft-inference";
import { NSLoading, NSMarkdown } from "namirasoft-site-react";
import { Component, ReactNode } from "react";
import Styles from "./Interaction.module.css";

interface InteractionProps
{
    interaction: InteractionRow;
    headerExtra?: ReactNode;
}

function formatMetaValue(value: any): string
{
    if (value === null || value === undefined)
        return "-";
    if (typeof value === "number")
    {
        if (Number.isInteger(value) && Math.abs(value) < 100_000)
            return value.toLocaleString();
        return value.toString();
    }
    if (typeof value === "object")
        return JSON.stringify(value);
    return String(value);
}

function formatMetaKey(key: string): string
{
    return key.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatDuration(ms: number): string
{
    if (!Number.isFinite(ms) || ms < 0)
        return "-";
    if (ms < 1000)
        return `${Math.round(ms)} ms`;
    let s = ms / 1000;
    if (s < 60)
        return `${s.toFixed(2)} s`;
    let m = Math.floor(s / 60);
    let r = s - m * 60;
    return `${m}m ${r.toFixed(1)}s`;
}

function formatCost(millicent: number): string
{
    if (!Number.isFinite(millicent))
        return "-";
    if (millicent === 0)
        return "$0";
    let fiat = PriceOperation.millicent_to_fiat(millicent, 5);
    if (fiat === 0)
        return "<$0.00001";
    return `$${fiat.toFixed(5)}`;
}

class Interaction extends Component<InteractionProps>
{
    override render()
    {
        let i = this.props.interaction;
        let userMessage = i.message_user;
        let userCreatedAtLabel = new Date(i.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
        let inProgress = i.status === InteractionStatus.Running;
        let failed = i.status === InteractionStatus.Failed || !!i.message_error;
        let showStats = !inProgress;
        let tokensPerSec = i.duration > 0 ? (i.total_token_output / (i.duration / 1000)) : 0;
        let updatedAtLabel = new Date(i.updated_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
        let metadataEntries = i.metadata && typeof i.metadata === "object" ? Object.entries(i.metadata) : [];
        let metadataScalars = metadataEntries.filter(([, v]) => !isPlainObject(v));
        let metadataObjects = metadataEntries.filter(([, v]) => isPlainObject(v));
        return (
            <div className={Styles.ns_interaction}>
                <div className={Styles.ns_user}>
                    <div className={Styles.ns_bubble_user}>{userMessage}</div>
                    <div className={Styles.ns_bubble_user_time}>{userCreatedAtLabel}</div>
                </div>
                <div className={Styles.ns_ai_head_row}>
                    <div className={Styles.ns_ai_head}>
                        <span className={Styles.ns_ai_provider}>{i.model_provider}</span>
                        <span className={Styles.ns_ai_dot}>·</span>
                        <span className={Styles.ns_ai_model}>{i.model_name}</span>
                        {
                            i.run_id &&
                            <>
                                <span className={Styles.ns_ai_dot}>·</span>
                                <span className={Styles.ns_ai_run_id} title={`Run ID: ${i.run_id}`}>Run ID: {i.run_id}</span>
                            </>
                        }
                    </div>
                    {this.props.headerExtra}
                </div>
                {
                    inProgress ?
                        <NSLoading title="Thinking" description="Awaiting a response from the model ..." /> :
                        failed ?
                            <div className={Styles.ns_bubble_error}>{i.message_error || "Something went wrong."}</div> :
                            <div className={Styles.ns_bubble_ai}><NSMarkdown text={i.message_ai} /></div>
                }
                <div className={Styles.ns_meta}>
                    <div className={Styles.ns_meta_row}>
                        <span className={Styles.ns_meta_item}>
                            {updatedAtLabel}
                        </span>
                        {
                            showStats &&
                            <>
                                <span className={Styles.ns_meta_item} title="Response duration">
                                    <strong>Duration:</strong> {formatDuration(i.duration)}
                                </span>
                                {
                                    tokensPerSec > 0 &&
                                    <span className={Styles.ns_meta_item} title="Output tokens per second">
                                        <strong>Speed:</strong> {tokensPerSec.toFixed(1)} tok/s
                                    </span>
                                }
                                {
                                    metadataScalars.map(([k, v]) =>
                                        <span key={k} className={Styles.ns_meta_item} title={`${k}: ${formatMetaValue(v)}`}>
                                            <strong>{formatMetaKey(k)}:</strong> {formatMetaValue(v)}
                                        </span>
                                    )
                                }
                            </>
                        }
                    </div>
                    {
                        showStats &&
                        <div className={Styles.ns_meta_table_wrap}>
                            <table className={Styles.ns_meta_table}>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>In</th>
                                        <th>Out</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <th scope="row">Tokens</th>
                                        <td>{i.total_token_input.toLocaleString()}</td>
                                        <td>{i.total_token_output.toLocaleString()}</td>
                                        <td>{i.total_token.toLocaleString()}</td>
                                    </tr>
                                    <tr>
                                        <th scope="row">Cost</th>
                                        <td>{formatCost(i.total_cost_input)}</td>
                                        <td>{formatCost(i.total_cost_output)}</td>
                                        <td>{formatCost(i.total_cost)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    }
                    {
                        showStats && metadataObjects.map(([k, v]) => renderMetaEntry(k, v))
                    }
                </div>
            </div>
        );
    }
}

function isPlainObject(v: any): v is Record<string, any>
{
    return v !== null && typeof v === "object" && !Array.isArray(v);
}

function renderMetaEntry(key: string, value: any)
{
    if (isPlainObject(value))
    {
        let entries = Object.entries(value);
        if (entries.length === 0)
            return null;
        return (
            <div key={key} className={Styles.ns_meta_table_wrap}>
                <table className={Styles.ns_meta_table}>
                    <thead>
                        <tr>
                            <th></th>
                            {entries.map(([k]) => <th key={k}>{formatMetaKey(k)}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th scope="row">{formatMetaKey(key)}</th>
                            {entries.map(([k, v]) => <td key={k}>{formatMetaValue(v)}</td>)}
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }
    return (
        <div key={key} className={Styles.ns_meta_row}>
            <span className={Styles.ns_meta_item} title={`${key}: ${formatMetaValue(value)}`}>
                <strong>{formatMetaKey(key)}:</strong> {formatMetaValue(value)}
            </span>
        </div>
    );
}

export default Interaction;